import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useInView, useScroll } from "framer-motion";
import {
  Shield, ArrowRight, CheckCircle, Brain, Users, BarChart2,
  Radar, GitBranch, ClipboardList, MessageCircle,
  Siren, Sword, ShieldCheck, Layers, HeartPulse, Server,
  Camera, UserSearch, Megaphone, Star, MapPin, Lock,
  Waves, Cctv, Radio, Building2, Clock, Flame,
  Navigation, LayoutDashboard, Languages, Minus, Plus,
} from "lucide-react";
import { GOOGLE_LIGHT } from "../data/googleColors";
import { usePreferences, FONT_SCALES } from "../context/PreferencesContext";

// Google-light palette minus blue — blue reads as a cool corporate accent that
// clashes with the saffron/maroon Kumbh theme, so every landing-page card
// cycles through the remaining 7 hues instead.
const CARD_COLORS = GOOGLE_LIGHT.filter((_, i) => i !== 0);
const cardColor = (i) => CARD_COLORS[i % CARD_COLORS.length];

// Stagger presets for cascading grid reveals.
const GRID_VARIANTS = { hidden: {}, show: { transition: { staggerChildren: 0.045 } } };
const ITEM_VARIANTS = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: "easeOut" } },
};
const LIFT_HOVER = { y: -5, scale: 1.02, transition: { type: "spring", stiffness: 320, damping: 22 } };

// "6,84,210" / "93.7%" / "8.4 min" / "842" -> a numeric target + formatter for
// count-up. Returns target: null for non-numeric values ("24/7") so CountUp
// just renders them statically instead of animating.
function parseCountTarget(raw) {
  const pct = raw.match(/^(\d+(?:\.\d+)?)%$/);
  if (pct) return { target: Number(pct[1]), format: (v) => `${v.toFixed(1)}%` };
  const min = raw.match(/^(\d+(?:\.\d+)?)\s*min$/);
  if (min) return { target: Number(min[1]), format: (v) => `${v.toFixed(1)} min` };
  if (/^\d+(,\d+)*$/.test(raw)) {
    return { target: Number(raw.replace(/,/g, "")), format: (v) => Math.round(v).toLocaleString("en-IN") };
  }
  return { target: null, format: () => raw };
}

// Animates a stat value up from 0 the first time it scrolls into view.
function CountUp({ value, className }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const { target, format } = parseCountTarget(value);
  const [display, setDisplay] = useState(target == null ? value : format(0));

  useEffect(() => {
    if (!inView || target == null) return;
    const duration = 1300;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(format(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView, target]);

  return <span ref={ref} className={className}>{display}</span>;
}

// ── Bilingual copy (English / Marathi) — mirrors the language toggle real
// Maharashtra government portals (like BMC's) ship as standard. ──
const COPY = {
  en: {
    skip: "Skip to main content",
    textSize: "Text size",
    navSignIn: "Officer Sign In",
    heroBadge: "Simhastha Kumbh 2027",
    heroLine1: "India's largest gathering.",
    heroEmphasis: "India's smartest",
    heroLine2: "command.",
    heroBody: "Kumbh AI brings every department — police, health, transport and more — onto one screen, so millions of pilgrims stay safe across Nashik & Trimbakeshwar, every single day.",
    ctaPrimary: "Access Command Console",
    ctaSecondary: "Explore 19 Modules",
    heroFootnote: "Authorised government personnel only · All access is logged",
    shahiHeading: "Getting ready for every Shahi Snan",
    shahiSub: "How ready we are for each holy bathing date and how many pilgrims are expected — updated in real time as the day gets closer.",
    tMinus: "T-minus",
    daysWord: "days",
    projectedCrowd: "Projected crowd:",
    readySuffix: "ready",
    pillarsHeading: "Built on three simple ideas",
    pillarsSub: "Kumbh AI doesn't replace the systems already in place — it double-checks them, adding a layer of trust and accountability on top.",
    infraHeading: "Everything running behind the scenes",
    infraSub: "The people, cameras and radios working quietly behind every decision.",
    modulesHeading: "19 modules. One console.",
    modulesSub: "From top-level planning to on-ground action, everything runs on one trusted, transparent platform.",
    agenciesHeading: "One screen, seven teams",
    trustHeading: "How we keep this trustworthy",
    finalBadge: "Simhastha Kumbh 2027 · Officer Access",
    finalHeading1: "Millions of pilgrims.",
    finalHeading2: "One shared screen.",
    finalBody: "Sign in with your authorised officer profile to see live crowd risk, coordinate every agency, and keep every pilgrim safe — in real time.",
    finalStat1: "19 AI modules live",
    finalStat2: "7 agencies united",
    finalStat3: "Human-reviewed, always",
    footerTagline: "Simhastha Kumbh 2027",
    footerLocation: "Nashik & Trimbakeshwar, Maharashtra",
    footerMeta: "Kumbh AI · v1.0 · Govt. of Maharashtra",
    navBrand: "Kumbh AI",
    navTagline: "Simhastha Kumbh 2027",
    /* The landing header carried a brand mark and a Sign In button and nothing
       between them, so a visitor had no way to reach any of this page's five
       sections except by scrolling the whole thing. These label that menu. */
    navShahi: "Shahi Snan",
    navPillars: "Capabilities",
    navInfra: "Infrastructure",
    navModules: "Modules",
    navAgencies: "Agencies",
  },
  mr: {
    skip: "मुख्य मजकुराकडे जा",
    textSize: "मजकूर आकार",
    navSignIn: "अधिकारी प्रवेश",
    heroBadge: "सिंहस्थ कुंभ 2027",
    heroLine1: "भारतातील सर्वात मोठा मेळावा.",
    heroEmphasis: "भारतातील सर्वात स्मार्ट",
    heroLine2: "नियंत्रण.",
    heroBody: "कुंभ एआय पोलीस, आरोग्य, वाहतूक यांसह सर्व विभागांना एका स्क्रीनवर आणते, जेणेकरून नाशिक व त्र्यंबकेश्वरमध्ये लाखो यात्रेकरू दररोज सुरक्षित राहतील.",
    ctaPrimary: "कमांड कन्सोलमध्ये प्रवेश करा",
    ctaSecondary: "19 मॉड्यूल्स पहा",
    heroFootnote: "केवळ अधिकृत शासकीय अधिकाऱ्यांसाठी · सर्व प्रवेशाची नोंद ठेवली जाते",
    shahiHeading: "प्रत्येक शाही स्नानासाठी सज्जता",
    shahiSub: "प्रत्येक पवित्र स्नान दिनासाठी आपली सज्जता आणि अपेक्षित यात्रेकरूंची संख्या — दिवस जवळ येताच रिअल टाइममध्ये अद्ययावत.",
    tMinus: "उर्वरित",
    daysWord: "दिवस",
    projectedCrowd: "अपेक्षित गर्दी:",
    readySuffix: "सज्ज",
    pillarsHeading: "तीन साध्या तत्त्वांवर आधारित",
    pillarsSub: "कुंभ एआय सध्याच्या यंत्रणांची जागा घेत नाही — ते त्यांची पुन्हा पडताळणी करते आणि त्यावर विश्वास व जबाबदारीचा एक स्तर जोडते.",
    infraHeading: "पडद्यामागे सर्व काही सुरळीत सुरू",
    infraSub: "प्रत्येक निर्णयामागे शांतपणे काम करणारे लोक, कॅमेरे आणि रेडिओ.",
    modulesHeading: "19 मॉड्यूल्स. एक कन्सोल.",
    modulesSub: "उच्चस्तरीय नियोजनापासून ते प्रत्यक्ष कृतीपर्यंत, सर्व काही एकाच विश्वासार्ह व पारदर्शक व्यासपीठावर चालते.",
    agenciesHeading: "एक स्क्रीन, सात संस्था",
    trustHeading: "आम्ही विश्वासार्हता कशी जपतो",
    finalBadge: "सिंहस्थ कुंभ 2027 · अधिकारी प्रवेश",
    finalHeading1: "लाखो यात्रेकरू.",
    finalHeading2: "एक समान स्क्रीन.",
    finalBody: "थेट गर्दी जोखीम पाहण्यासाठी, सर्व संस्थांशी समन्वय साधण्यासाठी आणि प्रत्येक यात्रेकरूला रिअल टाइममध्ये सुरक्षित ठेवण्यासाठी आपल्या अधिकृत अधिकारी प्रोफाइलने प्रवेश करा.",
    finalStat1: "19 एआय मॉड्यूल्स सक्रिय",
    finalStat2: "7 संस्था एकत्रित",
    finalStat3: "नेहमी मानवी पुनरावलोकन",
    footerTagline: "सिंहस्थ कुंभ 2027",
    footerLocation: "नाशिक व त्र्यंबकेश्वर, महाराष्ट्र",
    footerMeta: "कुंभ एआय · आवृत्ती 1.0 · महाराष्ट्र शासन",
    navBrand: "कुंभ एआय",
    navTagline: "सिंहस्थ कुंभ 2027",
    navShahi: "शाही स्नान",
    navPillars: "क्षमता",
    navInfra: "पायाभूत सुविधा",
    navModules: "मॉड्यूल्स",
    navAgencies: "संस्था",
  },
};

const HERO_STATS = [
  { label: { en: "Active Pilgrims Tracked", mr: "सक्रिय यात्रेकरू ट्रॅक केले" }, value: "6,84,210" },
  { label: { en: "AI Modules Live",         mr: "एआय मॉड्यूल्स सक्रिय" },        value: "19" },
  { label: { en: "AI Confidence",           mr: "एआय विश्वासार्हता" },           value: "93.7%" },
  { label: { en: "Avg. Response Time",      mr: "सरासरी प्रतिसाद वेळ" },         value: "8.4 min" },
];

const TICKER_ITEMS = [
  { en: "LIVE · Ramkund Ghat is 92% full — being watched closely", mr: "थेट · रामकुंड घाट 92% भरले — बारकाईने निरीक्षण सुरू" },
  { en: "LIVE · 6,84,210 pilgrims currently in Nashik & Trimbakeshwar", mr: "थेट · नाशिक व त्र्यंबकेश्वर येथे सध्या 6,84,210 यात्रेकरू" },
  { en: "20 AI systems running · 93.7% confidence in current readings", mr: "20 एआय प्रणाली कार्यरत · सध्याच्या नोंदींमध्ये 93.7% विश्वासार्हता" },
  { en: "7 agencies working together · one shared screen since 06:00", mr: "7 संस्था एकत्र काम करत आहेत · सकाळी 06:00 पासून एक समान स्क्रीन" },
  { en: "Next Shahi Snan in 32 days — 78% ready", mr: "पुढील शाही स्नान 32 दिवसांत — 78% सज्ज" },
  { en: "Privacy protected · every AI decision is reviewed by a person", mr: "गोपनीयता सुरक्षित · प्रत्येक एआय निर्णयाचे व्यक्तीकडून पुनरावलोकन" },
];

const SHAHI_SNAN = [
  { event: { en: "Shravan Purnima", mr: "श्रावण पौर्णिमा" },     date: "11 Jul 2027", days: 32, crowd: "9L",  readiness: 78 },
  { event: { en: "Simhastha Peak Snan", mr: "सिंहस्थ पर्वस्नान" }, date: "12 Aug 2027", days: 64, crowd: "12L", readiness: 62 },
  { event: { en: "Anant Chaturdashi", mr: "अनंत चतुर्दशी" },     date: "14 Sep 2027", days: 97, crowd: "8L",  readiness: 54 },
];

const PILLARS = [
  { icon: Shield, title: { en: "Safe by Design", mr: "रचनेतूनच सुरक्षित" },
    desc: { en: "Built to protect privacy at every step. AI never acts alone — a person always reviews every alert before anything happens.",
            mr: "प्रत्येक टप्प्यावर गोपनीयता जपण्यासाठी तयार केलेले. एआय कधीही स्वतःहून कृती करत नाही — कोणतीही कारवाई होण्यापूर्वी नेहमी एक व्यक्ती पडताळणी करते." } },
  { icon: Brain, title: { en: "Smart by AI", mr: "एआय मुळे स्मार्ट" },
    desc: { en: "Spots crowd risks up to 4 hours before they happen, so teams can act early instead of reacting after the fact.",
            mr: "गर्दीचे धोके घडण्याच्या 4 तास आधी ओळखते, त्यामुळे पथके नंतर प्रतिक्रिया देण्याऐवजी आधीच कृती करू शकतात." } },
  { icon: Users, title: { en: "One Team Across Agencies", mr: "सर्व संस्था, एक टीम" },
    desc: { en: "One shared screen for the CM's Office, Collector, Police, Health, NDRF, Municipal and Revenue teams — no more separate systems.",
            mr: "मुख्यमंत्री कार्यालय, जिल्हाधिकारी, पोलीस, आरोग्य, एनडीआरएफ, महानगरपालिका आणि महसूल विभागासाठी एक समान स्क्रीन — यापुढे स्वतंत्र प्रणाली नाहीत." } },
];

const INFRA_STATS = [
  { icon: Cctv,      value: "842",  label: { en: "CCTV Cameras Live", mr: "सीसीटीव्ही कॅमेरे सक्रिय" } },
  { icon: Radio,     value: "48",   label: { en: "Radio Mesh Points", mr: "रेडिओ मेश पॉइंट्स" } },
  { icon: Building2, value: "7",    label: { en: "Agencies Connected", mr: "जोडलेल्या संस्था" } },
  { icon: Clock,     value: "24/7", label: { en: "EOC Command Watch", mr: "ईओसी कमांड वॉच" } },
];

const MODULES = [
  { icon: LayoutDashboard, label: { en: "Command Overview", mr: "कमांड विहंगावलोकन" } },
  { icon: Star,          label: { en: "Executive Intelligence", mr: "एक्झिक्युटिव्ह इंटेलिजन्स" } },
  { icon: Radar,         label: { en: "Multi-Hazard Risk", mr: "बहु-धोका जोखीम" } },
  { icon: Sword,         label: { en: "War Room", mr: "वॉर रूम" } },
  { icon: BarChart2,     label: { en: "AI Assurance", mr: "एआय हमी" } },
  { icon: Users,         label: { en: "Crowd Risk Engine", mr: "गर्दी जोखीम इंजिन" } },
  { icon: GitBranch,     label: { en: "Digital Twin Sim", mr: "डिजिटल ट्विन सिम्युलेशन" } },
  { icon: ClipboardList, label: { en: "Field Copilot", mr: "फील्ड कोपायलट" } },
  { icon: MessageCircle, label: { en: "Pilgrim Guidance", mr: "यात्रेकरू मार्गदर्शन" } },
  { icon: UserSearch,    label: { en: "Missing Persons", mr: "हरवलेल्या व्यक्ती" } },
  { icon: Megaphone,     label: { en: "Public Comms & Trust", mr: "जनसंवाद व विश्वास" } },
  { icon: Siren,         label: { en: "Emergency Response", mr: "आपत्कालीन प्रतिसाद" } },
  { icon: Layers,        label: { en: "Service Stress", mr: "सेवा ताण" } },
  { icon: Navigation,    label: { en: "Mobility Intelligence", mr: "वाहतूक इंटेलिजन्स" } },
  { icon: HeartPulse,    label: { en: "Health & Epidemic", mr: "आरोग्य व साथरोग" } },
  { icon: Shield,        label: { en: "Disaster Management", mr: "आपत्ती व्यवस्थापन" } },
  { icon: Server,        label: { en: "Infrastructure Resilience", mr: "पायाभूत सुविधा लवचिकता" } },
  { icon: Camera,        label: { en: "Police Modernization", mr: "पोलीस आधुनिकीकरण" } },
  { icon: ShieldCheck,   label: { en: "DPDP & Governance", mr: "डीपीडीपी व प्रशासन" } },
];

const AGENCIES = [
  { en: "Nashik Police", mr: "नाशिक पोलीस" },
  { en: "NDRF", mr: "एनडीआरएफ" },
  { en: "Municipal Corporation (NMC)", mr: "महानगरपालिका (एनएमसी)" },
  { en: "Health Department", mr: "आरोग्य विभाग" },
  { en: "Transport (MSRTC)", mr: "परिवहन (एमएसआरटीसी)" },
  { en: "Revenue Department", mr: "महसूल विभाग" },
];

const TRUST = [
  { en: "DPDP 2023 Compliant", mr: "डीपीडीपी 2023 अनुरूप" },
  { en: "Privacy Protected", mr: "गोपनीयता सुरक्षित" },
  { en: "Humans Review Every Alert", mr: "प्रत्येक अलर्टचे मानवी पुनरावलोकन" },
  { en: "No Automatic Action Taken", mr: "कोणतीही स्वयंचलित कारवाई नाही" },
  { en: "Every Action Logged", mr: "प्रत्येक कृतीची नोंद" },
];


// Marigold-garland bunting strip — hung just under the nav, like festival toran across a mandap.
function ToranGarland({ className }) {
  return (
    <svg className={className} width="100%" height="26" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
      <defs>
        <pattern id="toran-pattern" x="0" y="0" width="34" height="26" patternUnits="userSpaceOnUse">
          <line x1="0" y1="1.5" x2="34" y2="1.5" stroke="currentColor" strokeOpacity="0.55" strokeWidth="1.5" />
          <path d="M6,1.5 L16,1.5 L11,18 Z" fill="currentColor" fillOpacity="0.9" />
          <path d="M23,1.5 L33,1.5 L28,13 Z" fill="currentColor" fillOpacity="0.55" />
        </pattern>
      </defs>
      <rect width="100%" height="26" fill="url(#toran-pattern)" />
    </svg>
  );
}

// Ghat temple-spire skyline silhouette — evokes Ramkund/Trimbakeshwar riverbank temples.
function TempleSkyline({ className }) {
  return (
    <svg className={className} viewBox="0 0 480 220" preserveAspectRatio="xMidYMax slice" xmlns="http://www.w3.org/2000/svg">
      <g fill="currentColor">
        <path d="M20,220 L20,150 L34,118 L48,150 L48,220 Z" />
        <path d="M80,220 L80,130 L98,95 L106,80 L114,95 L132,130 L132,220 Z" />
        <path d="M170,220 L170,160 L184,132 L198,160 L198,220 Z" />
        <path d="M230,220 L230,100 L250,60 L258,44 L266,60 L286,100 L286,220 Z" />
        <path d="M330,220 L330,150 L344,118 L358,150 L358,220 Z" />
        <path d="M390,220 L390,135 L406,102 L422,135 L422,220 Z" />
      </g>
    </svg>
  );
}

// Ghat scene — pilgrim silhouettes on the steps, diyas afloat on the river.
// A hand-drawn illustration standing in for a real photograph, since no
// licensed Kumbh Mela photography is available to embed here.
function GhatScene({ className }) {
  const pilgrimX = [90, 160, 230, 300, 380, 470, 560, 660, 760, 860, 960, 1060, 1150, 1230, 1300, 1360];
  const diyaX = [60, 140, 210, 290, 350, 430, 510, 590, 670, 740, 820, 900, 980, 1060, 1140, 1220, 1300, 1380];
  return (
    <svg className={className} viewBox="0 0 1440 170" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M0,86 C160,70 320,98 480,84 C640,70 800,96 960,82 C1120,68 1280,92 1440,80 L1440,170 L0,170 Z" fill="#C9820C" fillOpacity="0.16" />
      <path d="M0,104 C180,92 360,116 540,102 C720,88 900,112 1080,98 C1260,84 1350,100 1440,96 L1440,170 L0,170 Z" fill="#E3A322" fillOpacity="0.14" />
      <g fill="#E3A322">
        {diyaX.map((x, i) => <circle key={x} cx={x} cy={92 + (i % 3) * 8} r={i % 2 ? 3 : 4} />)}
      </g>
      <g fill="#7A1B2B" fillOpacity="0.55">
        {pilgrimX.map((x) => (
          <g key={x} transform={`translate(${x},50)`}>
            <circle cx="0" cy="0" r="5.5" />
            <path d="M-7,7 C-7,20 7,20 7,7 Z" />
          </g>
        ))}
      </g>
    </svg>
  );
}

// Live Nashik Kumbh footage behind the hero — the Ramkund Ghat camera feed
// already shipped in public/videos, so this is genuine Kumbh imagery the
// project owns outright rather than stock or illustration.
//
// Layering, outermost first:
//   1. the moving ghat footage, which is what the hero should read as;
//   2. the still photograph beneath it, painted immediately as the video's
//      poster so the first frame is never an empty saffron block, and left
//      showing if the video cannot play (decode failure, reduced motion,
//      a browser that blocks autoplay);
//   3. the SVG illustration below both, if neither asset loads at all.
// The hero therefore always has a real backdrop and never a broken box.
//
// The media runs at full strength on the right, where there is no copy, and is
// veiled by a saffron scrim on the left so the headline keeps its contrast.
// Tune HERO_MEDIA_OPACITY to trade clarity against text legibility.
// The shipped camera feeds, by what each one actually shows (the labels come
// from the operator console in public/dashboard_link.html):
//   C1 Bridge — Vehicle Inflow      traffic crossing the bridge   1.9MB
//   C2 Entry Gate — People In       pilgrims entering            17.7MB
//   C3 Event Ground — Crowd         crowd on the ground          32.5MB
//   C4 Exit Gate — People Out       pilgrims leaving              5.5MB
// The hero wants people, the closing band wants vehicles moving.
const HERO_PHOTO_SRC = "/images/nashik-kumbh-hero.jpg";
const HERO_VIDEO_CROWD = "/videos/C3.mp4";
const HERO_VIDEO_TRAFFIC = "/videos/C1.mp4";
const HERO_MEDIA_OPACITY = 1;

// Middle band: the real Trimbakeshwar temple gate, pilgrims crowding the
// street outside it during the 2015 Kumbh. A still, not a clip — none of the
// shipped camera feeds is shot at Trimbakeshwar, they are all Nashik ground
// cameras, so a genuine photograph of the place beats a stand-in that moves.
const TRIMBAK_PHOTO_SRC = "/images/trimbakeshwar-temple.jpg";

// Per-photo CC BY-SA 4.0 attribution. The licence obliges us to name the
// author and link the licence wherever each photo appears, so every band that
// shows one passes its own credit. Dropping a credit makes that photo's use
// non-compliant — swap the photo instead.
const CREDIT_KUMBH_HERO = {
  author: "Prashant Kharote",
  authorUrl: "https://commons.wikimedia.org/wiki/File:Simhastha_Kumbh_Mela_at_Nashik_in_Maharashtra_state.jpg",
  licence: "CC BY-SA 4.0",
  licenceUrl: "https://creativecommons.org/licenses/by-sa/4.0/",
};
const CREDIT_TRIMBAK = {
  author: "Prabirghose",
  authorUrl: "https://commons.wikimedia.org/wiki/File:Kumbhmela_Nashik_2015_-_Trimbakeshwar_temple_entrance.JPG",
  licence: "CC BY-SA 4.0",
  licenceUrl: "https://creativecommons.org/licenses/by-sa/4.0/",
  note: "cropped",
};

// Gold gradient for display headings sitting on the dark scrim. Pale highlight
// at the top-left falling to deep saffron, so the type reads as struck metal
// rather than flat yellow. `bg-clip-text` needs the transparent text colour to
// let the gradient through; the drop-shadow at each use site keeps the light
// end of the ramp legible where the footage behind it is bright.
// With the dark scrim gone, nothing dims the photo behind the copy, so every
// piece of text sitting on media carries its own shadow instead. Two stacked
// shadows: a tight dark halo that separates glyph edges from busy detail, and
// a wider soft one that lifts the whole block off bright sky.
const TEXT_ON_MEDIA =
  "[text-shadow:0_1px_3px_rgba(0,0,0,0.85),0_3px_14px_rgba(0,0,0,0.6)]";
const GOLD_SHADOW =
  "drop-shadow-[0_1px_3px_rgba(0,0,0,0.85)] drop-shadow-[0_3px_14px_rgba(0,0,0,0.55)]";

const GOLD_SOLID = "text-[#FFD42A]";

const GOLD_TEXT =
  "bg-gradient-to-br from-[#FFFBE6] via-[#FFE24D] to-[#F2A50C] bg-clip-text text-transparent";

// photo:  the still shown, and the poster the video paints over.
// video:  null for a photo-only band.
// credit: the CC BY-SA record for `photo`, or null when another band on the
//         page already credits that same file.
function HeroMedia({
  photo = HERO_PHOTO_SRC,
  video = HERO_VIDEO_CROWD,
  credit = CREDIT_KUMBH_HERO,
}) {
  const [photoFailed, setPhotoFailed] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);

  // Honour the OS "reduce motion" setting: those users get the still frame the
  // video would have shown, never a looping clip they cannot stop.
  const [reduceMotion, setReduceMotion] = useState(
    () => typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!mq) return;
    const onChange = (e) => setReduceMotion(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  /* The hero clip is 32 MB. On a phone on conference wifi that is the
     difference between a landing page and a spinner, and the still frame
     underneath carries the same picture — so on a metered or slow
     connection the video is simply never requested.
     
     Network Information API is Chromium-only; where it is absent the
     video plays as before, which is the right default for the desktop
     browsers this is demonstrated on. */
  const [saveData] = useState(() => {
    const c = typeof navigator !== "undefined" ? navigator.connection : null;
    if (!c) return false;
    return Boolean(c.saveData) || ["slow-2g", "2g", "3g"].includes(c.effectiveType);
  });

  // The video leads; the photo sits under it as the poster and as the resting
  // state whenever the clip cannot or should not play.
  const usePhoto = Boolean(photo) && !photoFailed;
  const useVideo = Boolean(video) && !videoFailed && !reduceMotion && !saveData;
  if (!usePhoto && !useVideo) return null;

  return (
    <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
      {usePhoto && (
        <img
          key={photo}
          src={photo}
          alt=""
          onError={() => setPhotoFailed(true)}
          className="absolute inset-0 w-full h-full object-cover object-center"
          style={{ opacity: HERO_MEDIA_OPACITY }}
        />
      )}
      {useVideo && (
        <video
          key={video}
          src={video}
          poster={usePhoto ? photo : undefined}
          autoPlay
          muted
          loop
          playsInline
          /* Was "auto", which fetched all 32 MB before the page had
             finished painting. "none" lets the poster render first and
             the clip stream in behind it. */
          preload="none"
          onError={() => setVideoFailed(true)}
          className="absolute inset-0 w-full h-full object-cover object-center"
          style={{ opacity: HERO_MEDIA_OPACITY }}
        />
      )}
      {/* No dark scrim — the imagery is meant to read clearly, so the only
          overlay left is a thin saffron tint that ties the bands to the brand
          without dimming the picture. Legibility is carried entirely by the
          text itself now (see TEXT_ON_MEDIA), not by darkening the backdrop. */}
      <div className="absolute inset-0 bg-saffron/10" />

      {/* Licence-required photo credit. Small and low-contrast so it stays out
          of the way, but present and readable, as CC BY-SA obliges. */}
      {credit && usePhoto && (
        <div className="absolute bottom-1.5 right-3 text-[9px] leading-none text-white/55 pointer-events-auto">
          <a href={credit.authorUrl} target="_blank" rel="noopener noreferrer" className="hover:text-white/80 underline-offset-2 hover:underline">
            {credit.author}
          </a>
          {" · "}
          <a href={credit.licenceUrl} target="_blank" rel="noopener noreferrer" className="hover:text-white/80 underline-offset-2 hover:underline">
            {credit.licence}
          </a>
          {credit.note ? ` · ${credit.note}` : ""}
        </div>
      )}
    </div>
  );
}

// Wave divider between the saffron hero and the light content below.
function WaveDivider({ fill }) {
  return (
    <svg viewBox="0 0 1440 60" className="block w-full h-[40px] md:h-[60px]" preserveAspectRatio="none">
      <path
        d="M0,32 C240,64 480,0 720,16 C960,32 1200,64 1440,32 L1440,60 L0,60 Z"
        fill={fill}
      />
    </svg>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const { scrollYProgress } = useScroll();
  const { lang, setLang, scaleIdx, increaseText, decreaseText } = usePreferences();
  const c = COPY[lang];
  // The festive "Yatra One" display face only has a proper look in Latin —
  // its Devanagari glyphs read as an unrelated, cartoonish style next to the
  // clean Noto Sans Devanagari subtitles below them. Headings fall back to
  // that same subtitle face in Marathi so title and subtitle read as one
  // consistent typeface pairing instead of two clashing fonts.
  const headingFont = lang === "mr" ? "" : "font-['Yatra_One']";

  return (
    <div className="min-h-screen bg-surface">
      {/* ── Scroll progress ── */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-[3px] bg-saffron z-50 origin-left"
        style={{ scaleX: scrollYProgress }}
      />

      {/* ── Accessibility / utility bar ── */}
      <div className="bg-slate-50 border-b border-slate-200 text-slate-600">
        <div className="max-w-6xl mx-auto px-5 md:px-8 h-9 flex items-center justify-between gap-4">
          <a
            href="#main-content"
            className="absolute -translate-y-16 focus:translate-y-0 focus:relative bg-saffron-dark text-white text-xs font-bold px-3 py-1.5 rounded-md transition-transform z-50"
          >
            {c.skip}
          </a>
          <div className="flex-1" />
          <div className="flex items-center gap-3 text-[11px] font-semibold">
            <span className="hidden sm:inline text-slate-400">{c.textSize}</span>
            <div className="flex items-center border border-slate-300 rounded-md overflow-hidden">
              <button
                type="button"
                aria-label="Decrease text size"
                onClick={decreaseText}
                disabled={scaleIdx === 0}
                className="w-6 h-6 flex items-center justify-center hover:bg-slate-200 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
              >
                <Minus size={11} />
              </button>
              <span className="w-6 h-6 flex items-center justify-center border-x border-slate-300 text-slate-700">A</span>
              <button
                type="button"
                aria-label="Increase text size"
                onClick={increaseText}
                disabled={scaleIdx === FONT_SCALES.length - 1}
                className="w-6 h-6 flex items-center justify-center hover:bg-slate-200 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
              >
                <Plus size={11} />
              </button>
            </div>
            <div className="flex items-center gap-1 border border-slate-300 rounded-md overflow-hidden">
              <Languages size={11} className="ml-2 text-slate-400" />
              <button
                type="button"
                onClick={() => setLang("en")}
                aria-pressed={lang === "en"}
                className={`px-2 h-6 transition-colors ${lang === "en" ? "bg-saffron-dark text-white" : "hover:bg-slate-200 text-slate-600"}`}
              >
                EN
              </button>
              <button
                type="button"
                onClick={() => setLang("mr")}
                aria-pressed={lang === "mr"}
                className={`px-2 h-6 transition-colors ${lang === "mr" ? "bg-saffron-dark text-white" : "hover:bg-slate-200 text-slate-600"}`}
              >
                मराठी
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Top nav ── */}
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl gov-gradient flex items-center justify-center shadow-sm">
              <span className="text-white font-black text-sm tracking-tight">K</span>
            </div>
            <div>
              <div className="font-black text-[15px] text-slate-900 leading-none tracking-tight">{c.navBrand}</div>
              <div className="text-[10px] text-slate-400 leading-none mt-0.5 hidden sm:block">{c.navTagline}</div>
            </div>
          </div>
          {/* Horizontal section menu. Hidden below md, where the page is a single
              scroll and a cramped row of five links helps nobody; the sections
              still carry their anchors for deep links. Points at this page's
              sections, not app routes — those need a session. */}
          <nav aria-label="Sections" className="hidden md:flex items-center gap-1">
            {[
              ["#shahi", c.navShahi],
              ["#pillars", c.navPillars],
              ["#infra", c.navInfra],
              ["#modules", c.navModules],
              ["#agencies", c.navAgencies],
            ].map(([href, label]) => (
              <a
                key={href}
                href={href}
                className="px-2.5 py-2 rounded-lg text-[13px] font-semibold text-slate-600 hover:text-saffron hover:bg-saffron/5 transition-colors"
              >
                {label}
              </a>
            ))}
          </nav>

          <button
            onClick={() => navigate("/login")}
            className="group inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold text-white bg-saffron hover:brightness-95 transition-all shadow-sm"
          >
            {c.navSignIn} <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
          </button>
        </div>
      </header>

      <main id="main-content">
      {/* ── Hero (saffron command-center — matches header colour) ── */}
      <section className="relative gov-gradient-hero overflow-hidden">
        <HeroMedia />
        <ToranGarland className="absolute top-0 left-0 text-white/80 pointer-events-none" />
        <TempleSkyline className="absolute bottom-0 right-0 w-full max-w-xl h-32 md:h-44 text-white/[0.09] pointer-events-none" />
        <motion.div
          className="absolute -top-32 -right-20 w-[28rem] h-[28rem] rounded-full bg-white/10 pointer-events-none"
          animate={{ y: [0, -18, 0], x: [0, 14, 0] }}
          transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute -bottom-24 -left-16 w-72 h-72 rounded-full bg-white/[0.06] pointer-events-none"
          animate={{ y: [0, 16, 0], x: [0, -10, 0] }}
          transition={{ duration: 11, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
        />

        <div className="relative max-w-6xl mx-auto px-5 md:px-8 pt-14 pb-14 md:pt-20 md:pb-20">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2.5 px-5 py-2.5 bg-white/15 border border-white/25 rounded-full mb-6">
              <Flame size={22} className={`${GOLD_SOLID} flex-shrink-0`} />
              <span className={`${headingFont} text-xl sm:text-2xl md:text-3xl font-bold tracking-tight ${GOLD_TEXT} ${GOLD_SHADOW}`}>
                {c.heroBadge}
              </span>
            </div>
            <h1 className={`${headingFont} ${GOLD_TEXT} ${GOLD_SHADOW} text-4xl sm:text-5xl md:text-6xl font-normal leading-[1.15] tracking-tight`}>
              {c.heroLine1}
              <br />
              <span className="relative inline-block">
                {/* Darker underline than the old white/25 bar, which washed
                    out now that the gold sits on a dark scrim. */}
                <span className="absolute inset-x-0 bottom-1.5 h-3 md:h-5 bg-black/30 -skew-x-6 rounded-sm" />
                <span className="relative">{c.heroEmphasis}</span>
              </span>{" "}
              {c.heroLine2}
            </h1>
            <p className={`mt-6 text-base md:text-lg leading-relaxed max-w-2xl ${GOLD_SOLID} ${TEXT_ON_MEDIA}`}>
              {c.heroBody}
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <button
                onClick={() => navigate("/login")}
                className="group inline-flex items-center gap-2 px-8 py-4 rounded-xl text-sm font-black text-saffron-dark bg-white hover:bg-white/90 transition-all shadow-xl hover:scale-[1.03] active:scale-[0.98]"
              >
                {c.ctaPrimary} <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </button>
              <a
                href="#modules"
                className={`inline-flex items-center gap-2 px-8 py-4 rounded-xl text-sm font-bold bg-black/25 border border-[#FFD42A]/50 hover:bg-black/40 transition-all hover:scale-[1.03] active:scale-[0.98] ${GOLD_SOLID}`}
              >
                {c.ctaSecondary}
              </a>
            </div>
            <p className={`mt-5 text-xs flex items-center gap-1.5 ${GOLD_SOLID} ${TEXT_ON_MEDIA}`}>
              <Lock size={12} /> {c.heroFootnote}
            </p>
          </div>

          {/* Hero stat row */}
          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4">
            {HERO_STATS.map((s, i) => (
              <motion.div
                key={s.label.en}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={LIFT_HOVER}
                transition={{ delay: i * 0.06, duration: 0.4 }}
                className="rounded-2xl bg-white/15 border border-white/25 p-4 backdrop-blur-sm"
              >
                <div className={`text-2xl md:text-3xl font-black leading-none tabular-nums ${GOLD_TEXT} ${GOLD_SHADOW}`}>
                  <CountUp value={s.value} />
                </div>
                <div className={`text-xs font-semibold mt-1.5 ${GOLD_TEXT} ${GOLD_SHADOW}`}>{s.label[lang]}</div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Live ticker marquee */}
        <div className="group relative border-t border-white/20 bg-saffron-dark/30 overflow-hidden whitespace-nowrap py-2.5">
          <div className="inline-block animate-ticker group-hover:[animation-play-state:paused]">
            {[...TICKER_ITEMS, ...TICKER_ITEMS].map((t, i) => (
              <span key={i} className={`text-[11px] font-medium mx-8 ${GOLD_TEXT} ${GOLD_SHADOW}`}>
                <span className="text-[#FFE24D]">●</span> {t[lang]}
              </span>
            ))}
          </div>
        </div>

        <WaveDivider fill="#F8FAFC" />
      </section>

      {/* ── Ghat scene — pilgrims &amp; floating diyas ── */}
      <section className="relative bg-saffron-light overflow-hidden">
        <GhatScene className="w-full h-[110px] md:h-[150px]" />
      </section>

      {/* ── Shahi Snan readiness ── */}
      <section id="shahi" className="max-w-6xl mx-auto px-5 md:px-8 py-14 md:py-16 -mt-1">
        <div className="max-w-2xl mb-8">
          <h2 className={`${headingFont} text-2xl md:text-3xl font-normal text-slate-900 tracking-tight`}>{c.shahiHeading}</h2>
          <p className="mt-3 text-slate-500 text-sm md:text-base leading-relaxed">
            {c.shahiSub}
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {SHAHI_SNAN.map((d, i) => {
            const clr = cardColor(i);
            return (
              <motion.div
                key={d.event.en}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                whileHover={LIFT_HOVER}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ delay: i * 0.08, duration: 0.4 }}
                className="rounded-2xl border shadow-card p-6"
                style={{ background: clr.bgLight, borderColor: clr.border }}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="text-[10px] font-black text-saffron-dark uppercase tracking-wider mb-1">{c.tMinus} {d.days} {c.daysWord}</div>
                    <div className="text-lg font-black text-slate-900">{d.event[lang]}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{d.date}</div>
                  </div>
                  <Waves size={22} className="text-saffron-dark flex-shrink-0" />
                </div>
                <div className="flex items-center justify-between text-xs font-semibold text-slate-600 mb-1.5">
                  <span>{c.projectedCrowd} <span className="text-slate-800 font-black">{d.crowd}</span></span>
                  <span className="text-saffron-dark">{d.readiness}% {c.readySuffix}</span>
                </div>
                <div className="h-2 rounded-full bg-white/60 overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: `${d.readiness}%` }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: i * 0.08 + 0.2 }}
                    className="h-2 rounded-full bg-saffron"
                  />
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* ── Three pillars ── */}
      <section id="pillars" className="bg-white border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-14 md:py-16">
          <div className="max-w-2xl mx-auto text-center mb-10">
            <h2 className={`${headingFont} text-2xl md:text-3xl font-normal text-slate-900 tracking-tight`}>{c.pillarsHeading}</h2>
            <p className="mt-3 text-slate-500 text-sm md:text-base leading-relaxed">
              {c.pillarsSub}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PILLARS.map(({ icon: Icon, title, desc }, i) => {
              const clr = cardColor(i);
              return (
                <motion.div
                  key={title.en}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  whileHover={LIFT_HOVER}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ delay: i * 0.1, duration: 0.4 }}
                  className="rounded-2xl border p-7 shadow-sm hover:shadow-md transition-shadow duration-200"
                  style={{ background: clr.bgLight, borderColor: clr.border }}
                >
                  <motion.div
                    className="w-14 h-14 rounded-2xl bg-white/70 flex items-center justify-center mb-5"
                    whileHover={{ rotate: [0, -8, 8, 0] }}
                    transition={{ duration: 0.5 }}
                  >
                    <Icon size={26} style={{ color: clr.text }} />
                  </motion.div>
                  <div className="text-lg font-black text-slate-900 mb-2">{title[lang]}</div>
                  <div className="text-sm text-slate-600 leading-relaxed">{desc[lang]}</div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Everything running behind the scenes (saffron — matches header colour) ── */}
      <section id="infra" className="relative gov-gradient-hero overflow-hidden">
        <HeroMedia photo={TRIMBAK_PHOTO_SRC} video={null} credit={CREDIT_TRIMBAK} />
        {/* The drifting white orbs and the white temple silhouette that used to
            sit here were glow against a flat saffron panel. Over a real
            photograph at full brightness they read as white smudges, and the
            silhouette duplicates the actual temple in the picture, so both are
            gone. The photograph is the decoration now. */}
        <div className="relative max-w-6xl mx-auto px-5 md:px-8 py-14 md:py-16">
          <div className="max-w-2xl mb-10">
            <h2 className={`${headingFont} ${GOLD_TEXT} ${GOLD_SHADOW} text-2xl md:text-3xl font-normal tracking-tight`}>{c.infraHeading}</h2>
            <p className={`mt-3 text-sm md:text-base leading-relaxed ${GOLD_TEXT} ${GOLD_SHADOW}`}>
              {c.infraSub}
            </p>
          </div>
          <motion.div
            className="grid grid-cols-2 md:grid-cols-4 gap-5"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-60px" }}
            variants={GRID_VARIANTS}
          >
            {INFRA_STATS.map(({ icon: Icon, value, label }) => (
              <motion.div
                key={label.en}
                variants={ITEM_VARIANTS}
                whileHover={LIFT_HOVER}
                className="rounded-2xl bg-black/30 border border-[#FFD42A]/30 p-5 backdrop-blur-sm"
              >
                <Icon size={20} className={`${GOLD_SOLID} mb-2.5`} />
                <div className={`text-3xl md:text-4xl font-black tabular-nums leading-none ${GOLD_TEXT} ${GOLD_SHADOW}`}>
                  <CountUp value={value} />
                </div>
                <div className={`text-xs font-semibold mt-2 ${GOLD_TEXT} ${GOLD_SHADOW}`}>{label[lang]}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Modules ── */}
      <section id="modules" className="max-w-6xl mx-auto px-5 md:px-8 py-14 md:py-16">
        <div className="max-w-2xl mb-8">
          <h2 className={`${headingFont} text-2xl md:text-3xl font-normal text-slate-900 tracking-tight`}>{c.modulesHeading}</h2>
          <p className="mt-3 text-slate-500 text-sm md:text-base leading-relaxed">
            {c.modulesSub}
          </p>
        </div>
        <motion.div
          className="grid grid-cols-2 md:grid-cols-4 gap-3"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          variants={GRID_VARIANTS}
        >
          {MODULES.map(({ icon: Icon, label }, i) => {
            const clr = cardColor(i);
            return (
              <motion.div
                key={label.en}
                variants={ITEM_VARIANTS}
                whileHover={LIFT_HOVER}
                className="flex items-center gap-3 rounded-xl p-3.5 border hover:shadow-md transition-shadow"
                style={{ background: clr.bgLight, borderColor: clr.border }}
              >
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-white/70">
                  <Icon size={16} style={{ color: clr.text }} />
                </div>
                <span className="text-xs font-bold text-slate-700 leading-tight">{label[lang]}</span>
              </motion.div>
            );
          })}
        </motion.div>
      </section>

      {/* ── Inter-agency trust + compliance ── */}
      <section id="agencies" className="bg-white border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-10 space-y-8">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Users size={18} className="text-saffron-dark" />
              <h3 className="text-sm font-black text-slate-900 uppercase tracking-wide">{c.agenciesHeading}</h3>
            </div>
            <div className="flex flex-wrap gap-2.5">
              {AGENCIES.map((a, i) => {
                const clr = cardColor(i);
                return (
                  <span
                    key={a.en}
                    className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold border"
                    style={{ background: clr.bgLight, borderColor: clr.border, color: clr.text }}
                  >
                    {a[lang]}
                  </span>
                );
              })}
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-4">
              <ShieldCheck size={18} className="text-saffron-dark" />
              <h3 className="text-sm font-black text-slate-900 uppercase tracking-wide">{c.trustHeading}</h3>
            </div>
            <div className="flex flex-wrap gap-2.5">
              {TRUST.map((t, i) => {
                const clr = cardColor(i + 3);
                return (
                  <span
                    key={t.en}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border"
                    style={{ background: clr.bgLight, borderColor: clr.border, color: clr.text }}
                  >
                    <CheckCircle size={12} />
                    {t[lang]}
                  </span>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ── Final CTA (full-bleed — matches header colour) ── */}
      <section className="relative gov-gradient-hero overflow-hidden">
        <HeroMedia video={HERO_VIDEO_TRAFFIC} credit={null} />
        <ToranGarland className="absolute top-0 left-0 text-white/70 pointer-events-none" />
        <TempleSkyline className="absolute bottom-0 left-0 w-full h-24 md:h-36 text-white/[0.08] pointer-events-none" />
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[36rem] h-[36rem] rounded-full bg-white/[0.05] pointer-events-none"
          animate={{ scale: [1, 1.08, 1] }}
          transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
        />

        <div className="relative max-w-3xl mx-auto px-5 md:px-8 py-20 md:py-28 text-center">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/15 border border-white/25 rounded-full mb-6">
            <Flame size={11} className={GOLD_SOLID} />
            <span className={`text-[10px] font-black tracking-wider uppercase ${GOLD_SOLID} ${TEXT_ON_MEDIA}`}>
              {c.finalBadge}
            </span>
          </div>

          <h2 className={`${headingFont} ${GOLD_TEXT} ${GOLD_SHADOW} text-3xl md:text-5xl font-normal tracking-tight leading-[1.15]`}>
            {c.finalHeading1}
            <br />
            {c.finalHeading2}
          </h2>
          <p className={`mt-5 text-sm md:text-lg max-w-xl mx-auto leading-relaxed ${GOLD_TEXT} ${GOLD_SHADOW}`}>
            {c.finalBody}
          </p>

          <button
            onClick={() => navigate("/login")}
            className="group mt-9 inline-flex items-center gap-2 px-9 py-4 rounded-xl text-sm font-black text-saffron-dark bg-white hover:bg-white/90 transition-all shadow-xl hover:scale-[1.03] active:scale-[0.98]"
          >
            {c.ctaPrimary} <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
          </button>

          <div className={`mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs font-semibold ${GOLD_SHADOW}`}>
            <span className={GOLD_TEXT}>{c.finalStat1}</span>
            <span className="w-1 h-1 rounded-full bg-[#FFE24D]/70" />
            <span className={GOLD_TEXT}>{c.finalStat2}</span>
            <span className="w-1 h-1 rounded-full bg-[#FFE24D]/70" />
            <span className={GOLD_TEXT}>{c.finalStat3}</span>
          </div>
        </div>
      </section>
      </main>

      {/* ── Footer ── */}
      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-400">
          <div className="flex items-center gap-2">
            <MapPin size={12} className="text-saffron-dark" />
            <span>
              <span className={`${headingFont} text-saffron-dark text-sm tracking-wide`}>{c.footerTagline}</span>
              {" "}· {c.footerLocation}
            </span>
          </div>
          <div>{c.footerMeta}</div>
        </div>
      </footer>
    </div>
  );
}
